import React, { useEffect, useRef, useState } from 'react';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { SortIcon } from '../ui/Icons';

import { SortByValue } from './helpers';

const SORT_OPTIONS: { value: SortByValue; label: string }[] = [
  { value: 'checkInDate', label: 'Check-in Date' },
  { value: 'checkOutDate', label: 'Check-out Date' }
];

interface SortDropdownProps {
  value: SortByValue | undefined;
  onChange: (value: SortByValue | undefined) => void;
}

export default function SortDropdown({ value, onChange }: SortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: SortByValue) => {
    onChange(value === option ? undefined : option);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        type={ButtonType.transparent}
        size={ButtonSize.small}
        classNameButton="p-[5px]"
        aria-label="Sort options"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <SortIcon size="18px" strokeWidth={value ? 2.5 : 1.8} />
      </Button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-50 py-2"
          role="menu"
          aria-label="Sort options"
        >
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-gilroy-bold text-neutral-600">Sort by</span>
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange(undefined);
                  setIsOpen(false);
                }}
                className="text-xs text-primary-600 hover:text-primary-700 font-gilroy-medium"
              >
                Clear
              </button>
            )}
          </div>
          <div className="py-1">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(opt.value)}
                className={`w-full flex items-center justify-between px-4 py-2 text-sm font-gilroy-medium transition-colors hover:bg-gray-50 ${
                  value === opt.value ? 'text-primary-600' : 'text-neutral-600'
                }`}
              >
                {opt.label}
                {value === opt.value && (
                  <svg
                    className="w-4 h-4 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
